import { Color, IconName, useNotifications, UseNotifications } from '@elearning-platform/ui';
import { QueryClient, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { TransProps, useTranslation } from 'react-i18next';

import { addFavouriteItem, removeFavouriteItem } from '../api/requests';
import { QueryKey } from '../enums';
import {
  AddFavouriteItemData,
  AddFavouriteItemResponse,
  RemoveFavouriteItemData,
  RemoveFavouriteItemResponse,
  Thumbnail,
  UseFavouriteItems,
} from '../interfaces';
import { KnowledgeBaseData, ThumbnailList } from '../modules/knowledgeBaseV2/knowledgeBaseV2.types';

export const useFavouriteItems: () => UseFavouriteItems = (): UseFavouriteItems => {
  const queryClient: QueryClient = useQueryClient();
  const { addToast }: UseNotifications = useNotifications();
  const { t }: TransProps<never> = useTranslation();

  const handleError: () => void = (): void => (
    addToast({ icon: IconName.Error, iconColor: Color.Red300, text: t('competence.app.useFavouriteItems.error') })
  );

  const { mutateAsync: addItemAsync }: UseMutationResult<AddFavouriteItemResponse, Error, AddFavouriteItemData> = useMutation({
    mutationFn: ({ type, id }: AddFavouriteItemData): Promise<AddFavouriteItemResponse> => addFavouriteItem(type, id),
    onSuccess: (_: AddFavouriteItemResponse, variables: AddFavouriteItemData): void => {
      queryClient.setQueryData(
        [QueryKey.KnowledgeBaseData],
        (oldData: KnowledgeBaseData): KnowledgeBaseData => ({
          ...oldData,
          elements: oldData.elements?.map((list: ThumbnailList): ThumbnailList => ({
            ...list,
            items: list.items?.map((item: Thumbnail): Thumbnail => item.id === variables.id
              ? { ...item, favorite: true }
              : item
            ),
          })),
        }));
      addToast({
        icon: IconName.CheckmarkCircle,
        iconColor: Color.Green300,
        text: t('competence.app.useFavouriteItems.addedToList'),
      });
    },
    onError: handleError,
  });

  const { mutateAsync: removeItemAsync }: UseMutationResult<RemoveFavouriteItemResponse, Error, RemoveFavouriteItemData> = useMutation({
    mutationFn: ({ type, id }: RemoveFavouriteItemData): Promise<RemoveFavouriteItemResponse> => removeFavouriteItem(type, id),
    onSuccess: (_: RemoveFavouriteItemResponse, variables: RemoveFavouriteItemData): void => {
      queryClient.setQueryData(
        [QueryKey.KnowledgeBaseData],
        (oldData: KnowledgeBaseData): KnowledgeBaseData => ({
          ...oldData,
          elements: oldData.elements?.map((list: ThumbnailList): ThumbnailList => ({
            ...list,
            items: list.items?.map((item: Thumbnail): Thumbnail => item.id === variables.id
              ? { ...item, favorite: false }
              : item
            ),
          })),
        }));
      addToast({
        icon: IconName.CheckmarkCircle,
        iconColor: Color.Green300,
        text: t('competence.app.useFavouriteItems.deletedFromList'),
      });
    },
    onError: handleError,
  });

  return { addItemAsync, removeItemAsync };
};
